// packages/core/src/learning/school-modules/efficient-market.ts
// ─────────────────────────────────────────────────────────────────────────
// Lens Pack for the 'efficient-market' school. LearningModule, surface 'school'.
//   module id   = 'school-efficient-market'  (matches exhibit nativeModuleId)
//   homeSchool  = 'efficient-market'
// PROMOTED: three authored lessons (above the divider in the index).
//
// Placeholder presentational fields (emoji, accentColor, tier, order) carried
// from the stub conventions — tune per-school in the presentation pass.
// ─────────────────────────────────────────────────────────────────────────

import type { LearningModule } from '../LearningTypes';

export const SCHOOL_EFFICIENT_MARKET_MODULE: LearningModule = {
	id: 'school-efficient-market',
	title: 'Efficient Market',
	description:
		'The lens that reads price as a crowd of information — why a true story is not an edge, why humility is a skill, and where market efficiency breaks down.',
	emoji: '📈',
	accentColor: '#6a4fa3',
	tier: 'growth',
	pillar: 'long-term-investing',
	order: 0,
	surface: 'school',
	homeSchool: 'efficient-market',
	lessons: [
		{
			id: 'school-efficient-market-01',
			title: 'The price heard the story first',
			description:
				'This lesson teaches why efficient market thinking treats price as a crowd of information, not just a number.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['efficient-market'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: [
				'efficient-market',
				'price-as-information',
				'market-humility',
			],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'price as information',
				'crowded opinions',
				'market humility',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'You hear a market story and it feels obvious. A company is popular. A trend is everywhere. A headline sounds huge. Efficient market thinking asks a colder question: how much of that is already in the price?',
				},
				{
					type: 'paragraph',
					text: 'This lens sees a market price as the result of many people, institutions, expectations, fears, and incentives fighting over information at the same time.',
				},
				{
					type: 'bullet_list',
					items: [
						'prices react fast',
						'obvious stories get crowded',
						'confidence is not edge',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is thinking a story is useful just because it is true.',
				},
				{
					type: 'example',
					text: 'A company announces strong growth and everyone online gets excited. The efficient market lens asks whether buyers already expected that growth before the announcement. If they did, the good news may not create the opportunity people think it does.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that this lens can make markets sound too clean. Prices can still get emotional, stretched, or wrong. The point is not that markets are perfect. The point is that outguessing them is harder than it feels.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use this lens as a reflection before trusting a market opinion. Ask what the price may already know, and what evidence would prove your view wrong.',
				},
				{
					type: 'key_takeaway',
					text: 'Efficient market thinking says a true story is not automatically a useful edge.',
				},
			],
			audioScript:
				'Efficient market thinking starts with a simple but uncomfortable idea: the price probably heard the story before you did. That does not mean you are unintelligent. It means markets are crowded. When you look at a stock, a fund, a sector, or a broad market, you are not looking at a quiet object waiting for your opinion. You are looking at a price created by millions of opinions, expectations, fears, incentives, and trades.\n\nThis lens is not saying prices are perfect. It is saying prices are competitive. That matters. A company can be great, and still not be a great opportunity at the current price. A trend can be real, and still be overhyped. A headline can be true, and still already be reflected in the market. Efficient market thinking separates truth from edge. A fact being true is not enough. The harder question is whether that fact is already known, expected, and priced in.\n\nImagine you hear that artificial intelligence will change the economy. That may be true. But if everyone else also believes it, then many prices may already include that excitement. The opportunity is not simply knowing the story. The opportunity would require knowing something important that the market has not priced correctly. That is a much higher bar. Most people skip that bar. They hear a story, feel smart for understanding it, and assume the market has not noticed.\n\nEfficient market thinking protects you from that kind of overconfidence. It slows down the rush from idea to action. It asks: who else knows this? How obvious is this? What expectations are already baked into the price? What would make this view wrong? Those questions are not meant to make you passive. They are meant to make you honest.\n\nThe blind spot is that the lens can go too far. Real markets are not machines of perfect reason. Humans panic. People chase trends. Institutions have incentives. Bubbles can form. Some prices can be wrong for a long time. A rigid efficient market thinker may dismiss every active idea too quickly or treat messy human behavior as less important than it is. That is the limit.\n\nBut the core lesson is still powerful for regular users. Most investing mistakes do not come from lacking opinions. They come from trusting opinions too easily. A person sees a chart going up, hears a convincing story, and feels like the move is obvious. Efficient market thinking responds: obvious to whom? If it is obvious to everyone, the price may already reflect it.\n\nInside Nisk, this worldview is useful because it changes the way users think before they test a plan. Instead of asking, what do I think will happen? ask, what is already expected? Instead of asking, is this story exciting? ask, do I have a real edge, or just a feeling? That small shift can save attention, reduce impulse, and make the user more focused on behavior, costs, and patience. The price is not just a number. It is a crowd talking back.',
			flashcards: [
				{
					id: 'school-efficient-market-01-fc-1',
					role: 'main_idea',
					front: 'What does price represent?',
					back: 'A crowd of expectations, information, fear, incentives, and trades.',
				},
				{
					id: 'school-efficient-market-01-fc-2',
					role: 'watch_out',
					front: 'What is the trap?',
					back: 'Thinking a true story is automatically a useful edge.',
				},
				{
					id: 'school-efficient-market-01-fc-3',
					role: 'use_it',
					front: 'What should you ask?',
					back: 'Ask what the price may already know.',
				},
			],
			applyAction: {
				type: 'noToolAction',
				label: 'Test your market opinion',
				prompt: 'Write one market story you believe, then write what the current price may already assume.',
			},
			revision: 1,
		},
		{
			id: 'school-efficient-market-02',
			title: 'Humility is an investing skill',
			description:
				'This lesson teaches why efficient market thinking treats humility as protection against expensive confidence.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['efficient-market'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['efficient-market', 'humility', 'cost-awareness'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'humility advantage',
				'cost awareness',
				'prediction discipline',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Most people do not lose money because they know nothing. They lose money because they know a little, feel certain, and act like the market is easier than it is.',
				},
				{
					type: 'paragraph',
					text: 'Efficient market thinking values humility because the market is full of smart people, fast information, professional tools, and emotional traps.',
				},
				{
					type: 'bullet_list',
					items: [
						'costs compound',
						'taxes matter',
						'timing mistakes hurt',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is judging a decision by how confident it feels instead of how strong the actual edge is.',
				},
				{
					type: 'example',
					text: 'Someone buys and sells often because they follow financial news every day. Even if some guesses are right, fees, taxes, bad timing, and stress can make the activity less useful than it felt.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that humility can become avoidance. Some users may use efficient market thinking to avoid learning, planning, or understanding risk at all. Calm does not mean careless.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, compare your plan against your actual numbers. Ask whether your future depends on repeated prediction, or on behavior you can control.',
				},
				{
					type: 'key_takeaway',
					text: 'Efficient market thinking treats overconfidence as a cost.',
				},
			],
			audioScript:
				'Efficient market thinking does not start by calling people dumb. It starts by saying the game is harder than it looks. That is a different message. The point is not that regular people cannot learn. The point is that markets are competitive, information moves quickly, and confidence is cheap. Anyone can have a strong opinion. Having an opinion that is both right and not already reflected in the price is much harder.\n\nThis is why humility is an investing skill. It protects you from treating the market like a personal intelligence test. Many people get pulled into investing through stories. They hear about a company. They watch a creator explain a trend. They see a chart. They read a thread that sounds smart. Suddenly, they feel like they understand something big. Efficient market thinking says: slow down. Feeling informed is not the same as having an edge.\n\nHumility also makes costs more visible. If you believe you can easily outguess the market, then fees, taxes, spreads, and timing mistakes may feel small. But if you accept that the edge is hard to find, then every cost matters more. Every unnecessary trade raises the bar. Every emotional move creates another chance to be wrong. Every hour spent chasing predictions has an attention cost. The efficient market lens makes those costs harder to ignore.\n\nThink about someone who checks financial news every morning and changes their plan constantly. They may feel responsible because they are paying attention. But attention is not automatically discipline. If the news cycle keeps pushing them into action, they may become reactive. They buy after excitement. They sell after fear. They mistake movement for control. Efficient market thinking pushes against that. It says that a calmer plan may be more honest than constant reaction.\n\nThe blind spot is important. Humility can turn into laziness if it becomes, markets are hard, so nothing matters. That is not the lesson. Behavior matters. Risk matters. Time matters. Costs matter. Diversification, savings rate, income, patience, and emotional control matter. Efficient market thinking is not permission to stop learning. It is a warning against pretending that prediction is easy.\n\nThis lens also does not say active investing can never work. Some people have skill, discipline, information, time, temperament, and process. But efficient market thinking asks users to be honest about whether they are one of those people, and what the cost of being wrong would be. That honesty is useful. It turns investing from ego into tradeoff.\n\nIn Nisk, this lesson should help the user ask a practical question: does my plan require me to be repeatedly clever, or does it mostly require me to be consistent? That question can change behavior. A person may decide to spend less energy on market guesses and more energy on controllable inputs. More saving. Less panic. Lower costs. Clearer rules. Better follow through.\n\nThe key is not to become passive. The key is to become less arrogant. Efficient market thinking says that overconfidence has a price, even when it feels exciting. Humility is not weakness here. It is risk control for your own brain.',
			flashcards: [
				{
					id: 'school-efficient-market-02-fc-1',
					role: 'main_idea',
					front: 'Why humility?',
					back: 'Markets are competitive, and confidence is easier than real edge.',
				},
				{
					id: 'school-efficient-market-02-fc-2',
					role: 'watch_out',
					front: 'What can humility become?',
					back: 'Avoidance, if it turns into not learning or planning.',
				},
				{
					id: 'school-efficient-market-02-fc-3',
					role: 'use_it',
					front: 'What should you compare?',
					back: 'Compare prediction heavy plans with behavior you can control.',
				},
			],
			applyAction: {
				type: 'openSimulator',
				label: 'Compare prediction with consistency',
			},
			revision: 1,
		},
		{
			id: 'school-efficient-market-03',
			title: 'Where efficient markets break down',
			description:
				'This lesson teaches the limits of efficient market thinking and why no market lens sees everything.',
			pillar: 'long-term-investing',
			schoolsOfThought: ['efficient-market'],
			estimatedReadMinutes: 5,
			estimatedListenMinutes: 6,
			difficulty: 3,
			tags: ['efficient-market', 'market-limits', 'imperfect-efficiency'],
			prerequisites: [],
			relatedLessons: [],
			lessonKind: 'school-core',
			concepts: [
				'market limits',
				'behavior in prices',
				'imperfect efficiency',
			],
			blocks: [
				{
					type: 'paragraph',
					text: 'Efficient market thinking is useful, but it can get too tidy. Real markets are full of humans, incentives, fear, greed, career risk, and crowd behavior.',
				},
				{
					type: 'paragraph',
					text: 'This lens works best when it teaches humility. It gets weaker when it acts like prices are always clean, rational, and complete.',
				},
				{
					type: 'bullet_list',
					items: [
						'bubbles can form',
						'panic can spread',
						'incentives can distort behavior',
					],
				},
				{
					type: 'callout',
					text: 'The common mistake is turning market humility into the claim that prices are always right.',
				},
				{
					type: 'example',
					text: 'During a panic, prices may fall because people need cash, fear spreads, or institutions are forced to sell. The efficient market lens can explain some of the reaction, but behavior still matters.',
				},
				{
					type: 'paragraph',
					text: 'The tradeoff is that once you admit markets can be wrong, it becomes tempting to believe you can always spot the mistake. Efficient market thinking keeps the bar high.',
				},
				{
					type: 'paragraph',
					text: 'In Nisk, use this lens to hold two thoughts at once: prices can be wrong, and outguessing them is still difficult.',
				},
				{
					type: 'key_takeaway',
					text: 'Efficient market thinking is strongest when it teaches humility, not perfection.',
				},
			],
			audioScript:
				'Efficient market thinking is powerful, but it has a danger: it can sound too clean. If you teach it badly, it starts to sound like markets are always right, prices are always rational, and human behavior barely matters. That is not a useful version of the lens. Real markets are made of people and institutions. People panic. Institutions manage career risk. Investors chase what recently worked. News spreads unevenly. Incentives shape behavior. Sometimes prices move in ways that look extreme because the humans behind them are extreme in that moment.\n\nSo the better version of efficient market thinking is not, prices are perfect. The better version is, prices are hard to outguess. That distinction matters. A price can be wrong and still difficult to profit from. A market can be emotional and still punish overconfident traders. A bubble can be obvious to some people and still continue longer than expected. A panic can create opportunity, but only for someone with cash, temperament, patience, and a plan. Knowing that markets have flaws does not automatically give you the skill to use those flaws.\n\nThis is the tension that makes the lens worth teaching. Efficient market thinking protects users from arrogance, but it should not blind them to reality. Markets have episodes where fear takes over. They have periods where stories become too popular. They have sectors where incentives get strange. They have moments where people buy because prices are rising, not because value is clear. Behavioral finance has a lot to say here. So do value investors, macro thinkers, and risk managers. Efficient market thinking should sit in debate with those schools, not pretend the debate is over.\n\nA realistic example is a market crash. Prices fall fast. Some sellers are scared. Some need cash. Some funds have rules that force selling. Some people simply cannot handle the uncertainty. Is the market processing information? Yes. Is it also processing fear and pressure? Also yes. The efficient market lens might say: be careful assuming you know the correct price in the middle of chaos. Another lens might say: behavior creates opportunity when people overreact. Both can notice something real.\n\nThe trap is jumping from markets are not perfect to I can easily find the mistakes. That leap is where many users get hurt. It feels empowering, but it may be just another form of confidence. Efficient market thinking keeps the bar high. If you believe the price is wrong, what do you know? Why might others disagree? What would make you change your mind? What is the cost if you are early, late, or mistaken?\n\nInside Nisk, this lesson matters because schools are not tribes. You do not need to worship market efficiency to learn from it. You also do not need to reject it just because markets are messy. The useful middle is sharper: prices can be wrong, humans can be emotional, and outguessing the market is still difficult.\n\nThat is the final takeaway. Efficient market thinking is strongest when it teaches humility. It becomes weak when it teaches perfection. Use the lens to respect the difficulty of the game, not to pretend the game has no flaws.',
			flashcards: [
				{
					id: 'school-efficient-market-03-fc-1',
					role: 'main_idea',
					front: 'Are prices always right?',
					back: 'No. The stronger lesson is that prices are hard to outguess.',
				},
				{
					id: 'school-efficient-market-03-fc-2',
					role: 'watch_out',
					front: 'What is the bad leap?',
					back: 'Assuming imperfect markets are easy to exploit.',
				},
				{
					id: 'school-efficient-market-03-fc-3',
					role: 'use_it',
					front: 'What should you hold together?',
					back: 'Prices can be wrong, and outguessing them is still difficult.',
				},
			],
			applyAction: {
				type: 'openDashboard',
				label: 'Ground the lens in your numbers',
			},
			revision: 1,
		},
	],
};
